import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import * as UserActions from "../../redux/actions/user.actions";
import "./Login.css";

const EyeOpen = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeClosed = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// Debe coincidir con la politica del backend (user.model.js): min 8 caracteres,
// 1 minuscula, 1 mayuscula, 1 numero, 1 caracter especial.
const PASSWORD_POLICY_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PASSWORD_POLICY_HINT = "Mínimo 8 caracteres, con al menos una minúscula, una mayúscula, un número y un carácter especial.";

const ResetPassword = () => {
  const loginState = useSelector((state) => state.Login);
  const { defConfig, defLang, usertoken, success, failed, error: reduxError } = loginState;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const domain = searchParams.get("domain");

  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [localSuccess, setLocalSuccess] = React.useState(false);
  const [localError, setLocalError] = React.useState("");

  React.useEffect(() => {
    if (usertoken) navigate("/");
  }, [usertoken]);

  React.useEffect(() => {
    if (!submitted) return;
    if (success) {
      setLocalSuccess(true);
      setLoading(false);
    }
    if (failed) {
      setLocalError(reduxError || "No se pudo actualizar la contraseña. Intenta nuevamente.");
      setLoading(false);
    }
    // Depende del objeto completo (no solo de success/failed) para no perderse
    // dos fallos consecutivos con el mismo valor booleano.
  }, [loginState]);

  const submitForm = (e) => {
    e.preventDefault();
    if (loading) return;
    setLocalError("");

    if (!PASSWORD_POLICY_REGEX.test(newPassword)) {
      setLocalError(PASSWORD_POLICY_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError("Las contraseñas no coinciden.");
      return;
    }

    setSubmitted(true);
    setLoading(true);
    dispatch(UserActions.ResetPassword({ domain, token, newPassword }));
  };

  const lbl = defConfig[defLang] || {};

  if (!token || !domain) {
    return (
      <div className="xflow-login-page">
        <div className="xflow-login-main">
          <div className="xflow-login-panel">
            <div className="xflow-login-logo-wrap">
              <img
                src={process.env.REACT_APP_CUSTOM_LOGO_IMAGE || "/images/logo.png"}
                onError={(e) => { e.currentTarget.src = "/images/logo.png"; }}
                alt={process.env.REACT_APP_COMPANY || "xFlow"}
                className="xflow-login-logo"
              />
            </div>
            <div className="xflow-login-brand">
              <span className="xflow-kicker">xFlow Workspace</span>
              <h2>Link inválido</h2>
              <p>Este link de recuperación está incompleto o dañado.</p>
            </div>
          </div>
          <div className="xflow-login-form-panel">
            <div className="xflow-login-card">
              <div className="xflow-login-title">
                <span className="xflow-kicker">xFlow Workspace</span>
                <h1>Link inválido</h1>
              </div>
              <p className="xflow-recovery-hint">
                Solicita un nuevo link de recuperación de contraseña.
              </p>
              <Link to="/forgot-password" className="xflow-recovery-back-btn">
                Solicitar nuevo link
              </Link>
            </div>
          </div>
        </div>
        <div className="xflow-login-copyright text-center">
          © 2023–2026 <b>xFlow PRO versión 2</b>
        </div>
      </div>
    );
  }

  return (
    <div className="xflow-login-page">
      <div className="xflow-login-main">

        {/* ── Panel izquierdo ── */}
        <div className="xflow-login-panel">
          <div className="xflow-login-logo-wrap">
            <img
              src={process.env.REACT_APP_CUSTOM_LOGO_IMAGE || "/images/logo.png"}
              onError={(e) => { e.currentTarget.src = "/images/logo.png"; }}
              alt={process.env.REACT_APP_COMPANY || "xFlow"}
              className="xflow-login-logo"
            />
          </div>
          <div className="xflow-login-brand">
            <span className="xflow-kicker">xFlow Workspace</span>
            <h2>Crea tu<br/>nueva contraseña</h2>
            <p>Elige una nueva contraseña segura para tu cuenta.</p>
          </div>
        </div>

        {/* ── Panel derecho (formulario) ── */}
        <div className="xflow-login-form-panel">
          <div className="xflow-login-mobile-logo">
            <img
              src={process.env.REACT_APP_CUSTOM_LOGO_IMAGE || "/images/logo.png"}
              onError={(e) => { e.currentTarget.src = "/images/logo.png"; }}
              alt={process.env.REACT_APP_COMPANY || "xFlow"}
            />
          </div>

          <div className="xflow-login-card">
            <div className="xflow-login-title">
              <span className="xflow-kicker">xFlow Workspace</span>
              <h1>Restablecer contraseña</h1>
            </div>

            {localSuccess ? (
              <div className="xflow-recovery-success">
                <span className="e-icons e-check-box xflow-recovery-check"></span>
                <p>
                  Tu contraseña fue actualizada correctamente.<br/>
                  Ya puedes ingresar con ella.
                </p>
                <Link to="/login" className="xflow-recovery-back-btn">
                  Ir al inicio de sesión
                </Link>
              </div>
            ) : (
              <form onSubmit={submitForm} autoComplete="off">
                <p className="xflow-recovery-hint">
                  Ingresa tu nueva contraseña para continuar.
                </p>

                {localError && (
                  <div className="xflow-login-error" style={{ marginBottom: 12 }}>
                    {localError}
                  </div>
                )}

                <div className="xfl-field">
                  <label htmlFor="rp-new-password">
                    {lbl.formnewpassword || "Nueva contraseña"}
                  </label>
                  <div className="xfl-password-wrap">
                    <input
                      id="rp-new-password"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Mínimo 8 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="xfl-eye-btn"
                      onClick={() => setShowNewPassword((v) => !v)}
                      aria-label={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                  <p className="xflow-recovery-hint" style={{ marginTop: 6, fontSize: "1.2rem" }}>
                    {PASSWORD_POLICY_HINT}
                  </p>
                </div>

                <div className="xfl-field">
                  <label htmlFor="rp-confirm-password">
                    {lbl.formconfirmpassword || "Confirmar contraseña"}
                  </label>
                  <div className="xfl-password-wrap">
                    <input
                      id="rp-confirm-password"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Repite tu nueva contraseña"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="xfl-eye-btn"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="e-btn e-primary xflow-login-submit"
                  disabled={loading}
                  style={{ opacity: loading ? 0.7 : 1, cursor: loading ? "wait" : "pointer" }}
                >
                  {loading
                    ? "Actualizando..."
                    : (lbl.resetpassword || "Actualizar contraseña")}
                </button>

                <div className="xflow-recovery-back">
                  <Link to="/login">← Volver al inicio de sesión</Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="xflow-login-copyright text-center">
        © 2023–2026 <b>xFlow PRO versión 2</b>
      </div>
    </div>
  );
};

export default ResetPassword;
